import baseApiClient from '@cnd_fe/ky-client'

// API용 클라이언트 생성
const client = baseApiClient.extend({
  prefixUrl: import.meta.env.VITE_RES_ADMIN_SERVICE_URL
})

// 브릿지 API용 클라이언트 (res-auth)
const authClient = baseApiClient.extend({
  prefixUrl: import.meta.env.VITE_RES_AUTH_SERVICE_URL
})

// 초기 데이터 모델
export const initialMenu = {
  menuId: null,
  menuNm: '',
  upMenuId: '',
  menuLink: '',
  menuType: '',
  sortSeq: null,
  useYn: 'Y',
  level: 1,
  menuSubttl: '',
  dmnId: '',
  prgrmId: '',
  processGbn: 'R',
  managerDept: '',
  managerName: '',
  managerTel: '',
  memo: '',
  menuIndctYn: 'Y', // 메뉴표시여부
  scrtyYn: 'N',
  menuContsT: '', // 컨텐츠상단 (메뉴 페이지 상단 HTML)
  menuContsB: '', // 컨텐츠하단 (메뉴 페이지 하단 HTML)
  navType:'L',     // Navigation
}

// 사용여부 옵션
export const useYnItems = [
  { value: 'Y', label: '사용' },
  { value: 'N', label: '미사용' }
]

/**
 * 메뉴 목록 조회
 * @param {object} [payload] - 검색 파라미터
 * @returns {Promise<any>}
 */
export const getMenuList = async (payload = {}) => {
  try {
    const result = await client.post('menu/menu-list', { json: payload }).json()
    return result
  } catch (error) {
    throw error
  }
}

/**
 * 도메인 목록 조회
 * @returns {Promise<any>}
 */
export const getDomainList = () => {
  return client.post(`menu/domain-list`, { json: {} }).json()
}


/**
 * 메뉴 상세 조회
 * @param {string} menuId - Menu ID
 * @returns {Promise<any>}
 */
export const getMenuById = (menuId) => {
  return client.post(`menu/menu-detail`, { json: { menuId } }).json()
}


/**
 * 메뉴 등록/수정 처리
 * @param {object} payload - 수정할 데이터
 * @returns {Promise<any>}
 */
export const procMenu = (payload) => {
  return client.post(`menu/menu-proc`, { json: payload }).json()
}


/**
 * 메뉴 삭제 처리
 * @param {string} menuId - 삭제할 Menu ID
 * @returns {Promise<any>}
 */
export const deleteMenu = async (menuId) => {
  // 메뉴 상세 정보 조회 후 삭제 처리
  const res = await client.post(`menu/menu-detail`, { json: { menuId } }).json();
  let data = {};
  if (res?.result) {
    data = res.data;
    data.scrtyYn = 'N';
    data.processGbn = 'D';
  }

  return client.post(`menu/menu-proc`, { json: data }).json();
}

/**
 * 메뉴 순서 변경
 * @author ehdwn17
 * @since 2026-04-10
 * @param {object} payload - { dmnId, menuId, targetMenuId }
 * @returns {Promise<any>}
 */
export const changeMenuSort = (payload) => {
  return client.post('menu/menu-sort-change', { json: payload }).json()
}

/**
 * 메뉴 엑셀 다운로드 (Blob 반환)
 * @author ehdwn17
 * @since 2026-04-10
 * @param {object} payload - { dmnId }
 * @returns {Promise<Blob>}
 */
export const downloadMenuExcelBlob = (payload) => {
  return client.post('menu/menu-excel', { json: payload }).blob()
}

/**
 * 포털 URL 생성 (pageUrl 우선, fallback으로 menuType + menuLink)
 * - pageUrl: BE 상세 API에서 SCMS_PRGRM_PAGE JOIN으로 생성된 실제 포털 경로
 * - menuLink: 원본 DB 값 (INPRG는 보통 비어있음)
 * - domainUrl: DB SCMS_DOMAIN.DMN 값 (운영/개발), 없으면 환경변수 fallback (로컬)
 * @author ehdwn17
 * @since 2026-04-13
 * @param {object} menu - 메뉴 데이터 { menuId, menuType, menuLink, pageUrl }
 * @param {string} [domainUrl] - SCMS_DOMAIN.DMN 값 (예: yeyak.daegu.go.kr)
 * @returns {string|null} 포털 전체 URL 또는 null (이동 불가)
 */
export const buildPortalUrl = (menu, domainUrl) => {
  // VITE_MENU_PREVIEW_ENABLED: true→환경변수 URL, false→DB 도메인 URL
  const useEnvUrl = import.meta.env.VITE_MENU_PREVIEW_ENABLED === 'true'
  let baseUrl = ''
  if (useEnvUrl) {
    baseUrl = import.meta.env.VITE_PORTAL_BASE_URL || ''
  }
  if (!baseUrl && domainUrl) {
    baseUrl = domainUrl.startsWith('http') ? domainUrl : `https://${domainUrl}`
  }
  if (!baseUrl || !menu) return null

  const { menuId, menuType, pageUrl, menuLink } = menu
  if (menuType === 'DIR') return null

  // 실제 포털 경로 결정 (pageUrl 우선, 없으면 menuLink fallback)
  const resolvedLink = pageUrl || menuLink || ''

  // LINK 타입이 외부 URL(http)이면 그대로 반환
  if (menuType === 'LINK' && resolvedLink.startsWith('http')) {
    return resolvedLink
  }

  // 경로가 있는 경우: menuType별 포털 경로 생성
  if (resolvedLink) {
    const lastSegment = resolvedLink.split('/').filter(Boolean).pop() || ''

    let path = resolvedLink
    if (menuType === 'BBS' && lastSegment) {
      path = `/board/list/${lastSegment}`
    } else if (menuType === 'CONTENT' && lastSegment) {
      path = `/cms/${lastSegment}`
    }

    const separator = path.includes('?') ? '&' : '?'
    return `${baseUrl}${path}${separator}menuId=${menuId}`
  }

  // 경로가 없는 경우: menuId만으로 포털 접근
  if (menuId) {
    return `${baseUrl}/?menuId=${menuId}`
  }

  return null
}

/**
 * 브릿지 로그인 URL 요청 (관리자 → 사용자 포털)
 * @author ehdwn17
 * @since 2026-04-13
 * @param {string} targetMbrLgnId - 대상 사용자 로그인 ID
 * @param {string} targetUrl - 이동할 포털 전체 URL
 * @returns {Promise<object>} { success, redirectUrl, reason }
 */
export const getBridgeLoginUrl = (targetMbrLgnId, targetUrl) => {
  const params = new URLSearchParams({ targetMbrLgnId, targetUrl })
  return authClient.post(`bridge/admin-to-user?${params.toString()}`).json()
}
